import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { todayStr } from '@/lib/utils';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Autocomplete from '@/components/Autocomplete';
import { UserPlus, Building, ClipboardList } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

export default function NewRecord() {
  const [clients, setClients] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', email: '', source: '', client_type: '' });
  const [propForm, setPropForm] = useState({ owner_name: '', address: '', source: '', is_new_capture: false });
  const [visitForm, setVisitForm] = useState({ type: 'visita', client_name: '', owner_name: '', property_address: '', date: todayStr(), comments: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/crm/all-clients').then(allClients => {
      const asClients = allClients.map(c => ({ name: c.name, phone: c.phone || '', email: c.email || '', id: c.id, _type: c.type, _lead_id: c.lead_id }));
      setClients(asClients);
    }).catch(() => { api.get('/clients').then(setClients).catch(() => {}); });
    api.get('/properties').then(setProperties).catch(() => {});
  }, []);

  const saveClient = async () => {
    if (!clientForm.name) return toast.error('El nombre es obligatorio');
    setSaving(true);
    try {
      if (clientForm._editing_id) {
        await api.put(`/clients/${clientForm._editing_id}`, { name: clientForm.name, phone: clientForm.phone, email: clientForm.email, source: clientForm.source });
        toast.success('Cliente actualizado exitosamente');
      } else {
        await api.post('/clients', clientForm);
        toast.success('Cliente creado exitosamente');
      }
      setClientForm({ name: '', phone: '', email: '', source: '', client_type: '' });
      const c = await api.get('/clients');
      setClients(c);
    } catch { toast.error('Error al guardar cliente'); }
    setSaving(false);
  };

  const saveProperty = async () => {
    if (!propForm.owner_name || !propForm.address) return toast.error('Propietario y direccion son obligatorios');
    setSaving(true);
    try {
      await api.post('/properties', propForm);
      toast.success('Propiedad creada exitosamente');
      setPropForm({ owner_name: '', address: '', source: '', is_new_capture: false });
      const p = await api.get('/properties');
      setProperties(p);
    } catch { toast.error('Error al crear propiedad'); }
    setSaving(false);
  };

  const saveVisit = async () => {
    if (!visitForm.date) return toast.error('La fecha es obligatoria');
    setSaving(true);
    try {
      await api.post('/visits', visitForm);
      toast.success('Registro creado exitosamente');
      setVisitForm({ type: 'visita', client_name: '', owner_name: '', property_address: '', date: todayStr(), comments: '' });
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const inputCls = "bg-[#0A1F1A] border-[#1A3B32] focus:border-[#A38560] focus:ring-1 focus:ring-[#A38560] h-11 text-white placeholder:text-white/30 rounded-xl";

  return (
    <div data-testid="new-record-page" className="space-y-6">
      <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Nuevo Registro</h1>

      <Tabs defaultValue="cliente" className="w-full">
        <TabsList className="bg-[#0A1F1A] border border-white/5 h-auto p-1 rounded-xl flex flex-wrap gap-1">
          <TabsTrigger value="cliente" data-testid="tab-cliente" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg px-3 py-2.5 flex-1 min-w-[100px]">
            <UserPlus size={15} strokeWidth={1.5} /> Cliente
          </TabsTrigger>
          <TabsTrigger value="propiedad" data-testid="tab-propiedad" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg px-3 py-2.5 flex-1 min-w-[100px]">
            <Building size={15} strokeWidth={1.5} /> Propiedad
          </TabsTrigger>
          <TabsTrigger value="visita" data-testid="tab-visita" className="data-[state=active]:bg-[#A38560] data-[state=active]:text-[#03110D] text-white/60 gap-2 text-sm rounded-lg px-3 py-2.5 flex-1 min-w-[100px]">
            <ClipboardList size={15} strokeWidth={1.5} /> Visita / Oferta
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cliente">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h2 className="font-outfit text-lg font-medium text-white">
              {clientForm._editing_id ? 'Editar Cliente Existente' : 'Crear Nuevo Cliente'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre *</Label>
                <Autocomplete items={clients} value={clientForm.name}
                  onChange={v => setClientForm({ ...clientForm, name: v, _editing_id: undefined })}
                  onSelect={c => setClientForm({ name: c.name, phone: c.phone || '', email: c.email || '', source: c.source || '', _editing_id: c.id })}
                  placeholder="Buscar o escribir nombre..." />
              </div>
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Celular</Label>
                <Input data-testid="client-phone" value={clientForm.phone} onChange={e => setClientForm({ ...clientForm, phone: e.target.value })}
                  placeholder="Numero de celular" className={inputCls} />
              </div>
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Correo</Label>
              <Input data-testid="client-email" value={clientForm.email} onChange={e => setClientForm({ ...clientForm, email: e.target.value })}
                placeholder="correo@ejemplo.com" className={inputCls} />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fuente</Label>
              <Select value={clientForm.source} onValueChange={v => setClientForm({ ...clientForm, source: v })}>
                <SelectTrigger data-testid="client-source" className={inputCls}><SelectValue placeholder="Seleccionar fuente" /></SelectTrigger>
                <SelectContent className="bg-[#0A1F1A] border-white/10">
                  <SelectItem value="Pauta">Pauta</SelectItem>
                  <SelectItem value="Referido">Referido</SelectItem>
                  <SelectItem value="RRSS Sebastian">RRSS Sebastian</SelectItem>
                  <SelectItem value="RRSS propias">RRSS propias</SelectItem>
                  <SelectItem value="Otras">Otras</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo de Cliente</Label>
              <Select value={clientForm.client_type} onValueChange={v => setClientForm({ ...clientForm, client_type: v })}>
                <SelectTrigger data-testid="client-type" className={inputCls}><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                <SelectContent className="bg-[#0A1F1A] border-white/10">
                  <SelectItem value="Comprador">Comprador</SelectItem>
                  <SelectItem value="Arrendatario">Arrendatario</SelectItem>
                  <SelectItem value="Propietario">Propietario</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 items-center">
              <button onClick={saveClient} disabled={saving} data-testid="save-client-btn"
                className="w-full sm:w-auto bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">
                {clientForm._editing_id ? 'Actualizar Cliente' : 'Guardar Cliente'}
              </button>
              {clientForm._editing_id && (
                <button onClick={() => setClientForm({ name: '', phone: '', email: '', source: '' })} data-testid="cancel-edit-client-btn"
                  className="text-sm text-white/50 hover:text-white transition-colors underline">
                  Cancelar edicion
                </button>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="propiedad">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h2 className="font-outfit text-lg font-medium text-white">Crear Nueva Propiedad</h2>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre del Propietario *</Label>
              <Autocomplete items={clients} value={propForm.owner_name}
                onChange={v => setPropForm({ ...propForm, owner_name: v })}
                onSelect={c => setPropForm({ ...propForm, owner_name: c.name, owner_id: c.id })}
                placeholder="Buscar cliente..." />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Direccion o Nombre del Edificio *</Label>
              <Autocomplete items={properties.map(p => ({ ...p, name: p.address }))} value={propForm.address}
                onChange={v => setPropForm({ ...propForm, address: v })}
                onSelect={p => setPropForm({ ...propForm, address: p.address || p.name, owner_name: p.owner_name || propForm.owner_name })}
                placeholder="Buscar o escribir direccion..." secondaryField="owner_name" />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fuente del Propietario</Label>
              <Select value={propForm.source} onValueChange={v => setPropForm({ ...propForm, source: v })}>
                <SelectTrigger data-testid="property-source" className={inputCls}><SelectValue placeholder="Seleccionar fuente" /></SelectTrigger>
                <SelectContent className="bg-[#0A1F1A] border-white/10">
                  <SelectItem value="Pauta">Pauta</SelectItem>
                  <SelectItem value="Referido">Referido</SelectItem>
                  <SelectItem value="RRSS Sebastian">RRSS Sebastian</SelectItem>
                  <SelectItem value="RRSS propias">RRSS propias</SelectItem>
                  <SelectItem value="Otras">Otras</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-3 py-2">
              <Checkbox
                id="is_new_capture"
                data-testid="is-new-capture-checkbox"
                checked={propForm.is_new_capture}
                onCheckedChange={checked => setPropForm({ ...propForm, is_new_capture: checked })}
                className="border-[#A38560] data-[state=checked]:bg-[#A38560] data-[state=checked]:border-[#A38560]"
              />
              <Label htmlFor="is_new_capture" className="text-sm font-medium text-white cursor-pointer">
                Esta es una nueva captacion
              </Label>
            </div>
            <button onClick={saveProperty} disabled={saving} data-testid="save-property-btn"
              className="w-full sm:w-auto bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">
              Guardar Propiedad
            </button>
          </div>
        </TabsContent>

        <TabsContent value="visita">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-6 mt-4 space-y-4">
            <h2 className="font-outfit text-lg font-medium text-white">Registrar Visita u Oferta</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Tipo</Label>
                <Select value={visitForm.type} onValueChange={v => setVisitForm({ ...visitForm, type: v })}>
                  <SelectTrigger data-testid="visit-type" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1F1A] border-white/10">
                    <SelectItem value="visita">Visita</SelectItem>
                    <SelectItem value="oferta">Oferta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Fecha</Label>
                <Input data-testid="visit-date" type="date" value={visitForm.date} onChange={e => setVisitForm({ ...visitForm, date: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre del Cliente</Label>
                <Autocomplete items={clients} value={visitForm.client_name}
                  onChange={v => setVisitForm({ ...visitForm, client_name: v })}
                  onSelect={c => setVisitForm({ ...visitForm, client_name: c.name })}
                  placeholder="Buscar cliente..." />
              </div>
              <div>
                <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Nombre del Propietario</Label>
                <Autocomplete items={clients} value={visitForm.owner_name}
                  onChange={v => setVisitForm({ ...visitForm, owner_name: v })}
                  onSelect={c => setVisitForm({ ...visitForm, owner_name: c.name })}
                  placeholder="Buscar propietario..." />
              </div>
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Direccion del Inmueble</Label>
              <Autocomplete items={properties} value={visitForm.property_address} displayField="address"
                onChange={v => setVisitForm({ ...visitForm, property_address: v })}
                onSelect={p => setVisitForm({ ...visitForm, property_address: p.address })}
                placeholder="Buscar propiedad..." />
            </div>
            <div>
              <Label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 block">Comentarios</Label>
              <Textarea data-testid="visit-comments" value={visitForm.comments} onChange={e => setVisitForm({ ...visitForm, comments: e.target.value })}
                placeholder="Notas de la visita u oferta..." className="bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] min-h-[80px] rounded-xl" />
            </div>
            <button onClick={saveVisit} disabled={saving} data-testid="save-visit-btn"
              className="w-full sm:w-auto bg-gradient-to-r from-[#A38560] to-[#8B6F47] hover:from-[#8B6F47] hover:to-[#725A3A] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-[#A38560]/20 transition-all active:scale-[0.98] disabled:opacity-50">
              Guardar Registro
            </button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
